import { PlanungAdminRoles, PlanungViewerRoles } from '@bp/pim-auth-constants';
import { BadgeCardWidth } from '@bp/ui-components';
import { Day } from '../components/AvailabilityMatrix/AvailabilityMatrix';
import { HolidayType } from '../components/Holidays/graphql';
import { LessonFormType, LessonFormUnitType, VersionLessonFormType } from '../components/Lessons/types';
import { Time } from '../stores/TimetableVersion/TimeAvailabilityStore';
import { FilterLoaderArgs } from './routes-loader/filterLoader';

export function isPlainOldObject(possibleObject?: unknown): possibleObject is Record<string, unknown | undefined> {
  return !!possibleObject && typeof possibleObject === 'object' && !Array.isArray(possibleObject);
}

export function isFilterType(obj: unknown): obj is FilterLoaderArgs {
  return (
    isNotEmpty(obj) && isPlainOldObject(obj) && typeof obj.column === 'string' && typeof obj.filterValue === 'string'
  );
}
// https://github.com/colinhacks/zod/blob/v4/packages/zod/src/v3/types.ts#L599
export function isUuid(value: unknown): value is string {
  if (typeof value !== 'string') return false;
  const uuidRegex = /^[0-9a-fA-F]{8}\b-[0-9a-fA-F]{4}\b-[0-9a-fA-F]{4}\b-[0-9a-fA-F]{4}\b-[0-9a-fA-F]{12}$/i;
  return uuidRegex.test(value);
}

export function isArrayOf<T>(array: unknown[]): array is T[] {
  return Array.isArray(array) && array.every((a) => isNotEmpty(a));
}

export function isNotEmpty<T>(value: T | null | undefined): value is T {
  return value !== null && value !== undefined;
}

export function isData(obj: unknown): obj is { data: unknown } {
  return isNotEmpty(obj) && isPlainOldObject(obj) && typeof obj.data === 'object';
}

export function hasDurationCountArray(
  obj: unknown,
): obj is Required<Pick<VersionLessonFormType, 'uuid' | 'durationCountArray'>> {
  return isPlainOldObject(obj) && obj.durationCountArray !== undefined;
}

export function hasLessonUnit(obj: unknown): obj is LessonFormType & { lessonUnit: LessonFormUnitType[] } {
  return isPlainOldObject(obj) && obj.lessonUnit !== undefined && Array.isArray(obj.lessonUnit);
}

export function isDay(value?: string | null): value is Day {
  if (!value) return false;
  return ['mon', 'tue', 'wed', 'thu', 'fri', 'sat', 'sun'].includes(value);
}

export function getTypeChecktArray<T>(array: unknown[]): T[] {
  const _res: T[] = [];
  if (Array.isArray(array) && isArrayOf<T>(array)) {
    array.forEach((a) => {
      if (isNotEmpty(a)) {
        _res.push(a);
      }
    });
  }
  return _res;
}

export function isHoliday(value: unknown): value is HolidayType {
  return isPlainOldObject(value) && value.__typename === 'HolidayItem';
}

export function isBadgeCardWidth(value: unknown): value is BadgeCardWidth {
  if (Number.isInteger(value)) {
    return [0, 1, 2, 3, 4, 5, 6].includes(value as number);
  }
  return false;
}

export function isValideTimeString(value: unknown): value is Time {
  return isNotEmpty(value) && typeof value === 'string' && value.match(/^(0[0-9]|1[0-9]|2[0-3]):[0-5][0-9]$/) !== null;
}

export function isPlanungPermission(value: string): value is PlanungAdminRoles | PlanungViewerRoles {
  return (
    Object.values(PlanungAdminRoles).includes(value as PlanungAdminRoles) ||
    Object.values(PlanungViewerRoles).includes(value as PlanungViewerRoles)
  );
}

export function isPlanungDeputatePermission(
  value: string,
): value is PlanungAdminRoles.DeputatAdmin | PlanungViewerRoles.DeputatViewer {
  return value === PlanungAdminRoles.DeputatAdmin || value === PlanungViewerRoles.DeputatViewer;
}

export function isPlanungTimetablePermission(
  value: string,
): value is PlanungAdminRoles.StundenAdmin | PlanungViewerRoles.StundenViewer {
  return value === PlanungAdminRoles.StundenAdmin || value === PlanungViewerRoles.StundenViewer;
}

export function isPlanungSubstitutionPermission(
  value: string,
): value is PlanungAdminRoles.VertretungAdmin | PlanungViewerRoles.VertretungViewer {
  return value === PlanungAdminRoles.VertretungAdmin || value === PlanungViewerRoles.VertretungViewer;
}
